// PillarsSchema — the 4+1 pillar strip that anchors the new geoVINUM narrative.
// MAPS → Je vois · ACADEMY → J'apprends · AI → Je comprends · TASTE → Je déguste · IFRAME → J'intègre
// Horizontal row on desktop (visual chain), vertical stack on mobile.
// i18n : dictionnaire inline COPY (fr/en/es/pt).
import { Link } from "react-router-dom";
import { Map, GraduationCap, Sparkles, Wine, Frame, ArrowRight } from "lucide-react";
import { useLang } from "@/lib/i18n";

const COPY = {
  fr: {
    eyebrow: "Une expérience autour du vin",
    h2_a: "Comprendre le vin.", h2_b: "Visuellement.", h2_c: "Intelligemment.",
    sub: "Une carte pour voir. Une Academy pour apprendre. Une IA pour comprendre. Un carnet pour déguster. Une technologie pour intégrer le terroir partout.",
    verbs: { maps: "Je vois", academy: "J\u2019apprends", ai: "Je comprends", taste: "Je déguste", iframe: "J\u2019intègre" },
    discover: "Découvrir",
  },
  en: {
    eyebrow: "A wine experience",
    h2_a: "Understand wine.", h2_b: "Visually.", h2_c: "Intelligently.",
    sub: "A map to see. An Academy to learn. An AI to understand. A journal to taste. A technology to embed terroir everywhere.",
    verbs: { maps: "I see", academy: "I learn", ai: "I understand", taste: "I taste", iframe: "I embed" },
    discover: "Discover",
  },
  es: {
    eyebrow: "Una experiencia en torno al vino",
    h2_a: "Comprende el vino.", h2_b: "Visualmente.", h2_c: "Con inteligencia.",
    sub: "Un mapa para ver. Una Academy para aprender. Una IA para comprender. Un cuaderno para catar. Una tecnología para integrar el terruño en todas partes.",
    verbs: { maps: "Yo veo", academy: "Yo aprendo", ai: "Yo comprendo", taste: "Yo cato", iframe: "Yo integro" },
    discover: "Descubrir",
  },
  pt: {
    eyebrow: "Uma experiência em torno do vinho",
    h2_a: "Compreender o vinho.", h2_b: "Visualmente.", h2_c: "Com inteligência.",
    sub: "Um mapa para ver. Uma Academy para aprender. Uma IA para compreender. Um caderno para provar. Uma tecnologia para integrar o terroir em todo o lado.",
    verbs: { maps: "Eu vejo", academy: "Eu aprendo", ai: "Eu compreendo", taste: "Eu provo", iframe: "Eu integro" },
    discover: "Descobrir",
  },
};

const PILLARS = [
  { key: "maps",    icon: Map,           label: "Maps",    color: "#B23A82", to: "/cartes",           testId: "pillar-maps" },
  { key: "academy", icon: GraduationCap, label: "Academy", color: "#B23A82", to: "/academy",          testId: "pillar-academy" },
  { key: "ai",      icon: Sparkles,      label: "AI",      color: "#B23A82", to: "/geovinum-ai",      testId: "pillar-ai" },
  { key: "taste",   icon: Wine,          label: "Taste",   color: "#B23A82", to: "/geovinum-ai#taste", testId: "pillar-taste" },
  { key: "iframe",  icon: Frame,         label: "Iframe",  color: "#B23A82", to: "/pro",              testId: "pillar-iframe" },
];

const PillarsSchema = () => {
  const lang = useLang();
  const c = COPY[lang] || COPY.fr;
  return (
    <section className="py-20 md:py-28 bg-[color:var(--gv-mist)]" data-testid="home-pillars">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="text-center mb-14">
          <div className="text-[11px] uppercase tracking-widest text-[color:var(--gv-pink)] mb-4">
            {c.eyebrow}
          </div>
          <h2 className="font-display text-[32px] md:text-[44px] leading-[1.1] text-[color:var(--gv-ink)] max-w-3xl mx-auto">
            {c.h2_a} <span className="italic text-[color:var(--gv-pink)]">{c.h2_b}</span> <span className="italic text-[color:var(--gv-pink)]">{c.h2_c}</span>
          </h2>
          <p className="text-[15.5px] text-[color:var(--gv-ink)]/70 mt-5 max-w-2xl mx-auto">
            {c.sub}
          </p>
        </div>

        {/* Pillar chain: 5 tiles connected by discreet arrows on desktop */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 md:gap-2 relative">
          {PILLARS.map((p, i) => {
            const Icon = p.icon;
            return (
              <div key={p.key} className="relative">
                <Link
                  to={p.to}
                  className="group block bg-white border border-[color:var(--gv-line)] rounded-[6px] p-5 md:p-6 hover:border-[color:var(--gv-pink)] hover:shadow-[0_18px_40px_-24px_rgba(46,80,103,0.35)] transition-all reveal"
                  data-testid={p.testId}
                >
                  <div className="w-11 h-11 rounded-full bg-[color:var(--gv-pink)]/10 text-[color:var(--gv-pink)] flex items-center justify-center mb-4 group-hover:bg-[color:var(--gv-pink)] group-hover:text-white transition-colors">
                    <Icon size={20} strokeWidth={1.75} />
                  </div>
                  <div className="text-[10.5px] uppercase tracking-widest text-[color:var(--gv-ink)]/50 mb-1">{p.label}</div>
                  <div className="font-display text-[19px] md:text-[21px] text-[color:var(--gv-ink)] leading-tight">
                    {c.verbs[p.key]}
                  </div>
                  <div className="mt-4 inline-flex items-center gap-1 text-[12px] text-[color:var(--gv-pink)]/80 group-hover:text-[color:var(--gv-pink)]">
                    {c.discover} <ArrowRight size={12} />
                  </div>
                </Link>
                {/* Connector arrow between pillars — desktop only, subtle */}
                {i < PILLARS.length - 1 && (
                  <div className="hidden md:flex absolute top-1/2 -right-1 translate-x-1/2 -translate-y-1/2 z-10 items-center">
                    <div className="w-4 h-px bg-[color:var(--gv-ink)]/15" />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default PillarsSchema;
